// ─── Orphan-row cleanup on failed byte upload ────────────────────────────────
// The worker's signed-upload endpoints (`/photos/upload-url` and
// `/upload-link/public/:token/photo-upload-url`) INSERT the photo row before
// handing back a signed URL — they need the row's UUID to build the storage
// path. The client then PUTs the bytes to that URL. If the PUT fails (flaky
// network, iOS suspending the tab mid-read of an iCloud photo, etc.) the row
// remains with no bytes behind it: an "orphan". Orphans inflate the admin
// photo count and surface in the editor as broken/empty slots — a cell that
// "had a photo" now showing the empty add-a-photo state.
//
// `putOrCleanupOrphan` wraps the PUT so a failure best-effort-deletes the
// just-created row before re-throwing. Dependency-injected (PUT + delete passed
// in) so all four upload paths share one tested behaviour. The cleanup is
// best-effort: a failed delete (e.g. the same network drop that failed the PUT)
// never masks the original upload error, and the caller's existing catch still
// runs to mark the file errored.

/**
 * Run `put`. If it rejects, best-effort delete the orphaned photo row via
 * `deleteOrphan(photoId)` (swallowing any delete error) and then re-throw the
 * ORIGINAL upload error. When `photoId` is falsy there is nothing to orphan, so
 * cleanup is skipped.
 */
export async function putOrCleanupOrphan(
  photoId: string | null | undefined,
  put: () => Promise<void>,
  deleteOrphan: (photoId: string) => Promise<unknown>,
): Promise<void> {
  try {
    await put();
  } catch (uploadError) {
    if (photoId) {
      try {
        await deleteOrphan(photoId);
      } catch {
        // Best-effort only — never let a cleanup failure mask the real error.
      }
    }
    throw uploadError;
  }
}
